/*
 *
 * ChannelsPage
 *
 */

import React from "react";
import { connect } from "react-redux";
import { postChannelRequest } from "./actions";
import { createStructuredSelector } from "reselect";
import { isString, isObject } from "utils/helpers";
import Forms from "./Forms";
import ListError from "components/ListError";
import Loader from "components/Loader";
import SubscriptionRequiredNotice from "components/SubscriptionRequiredNotice";
import { colors } from "globalStyles";
import { fetchSession } from "containers/App/actions";
import { selectAllChannels, selectClients } from "./selectors";
import {
  selectChannelManagementBlocked,
  selectSessionLoadFailed,
  selectSessionUserLoaded,
} from "containers/App/selectors";
import { useForm, useWatch } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
const urlFix = (url) => (url && url.slice(-11) !== "missing.png" ? url : null);
const makeUrl = (file) => window.URL.createObjectURL(file);
const getBrandFormData = (substate) => {
  const urlFix = (url) =>
    url && (isObject(url) || !url.includes("missing.png")) ? url : null;
  const keys = [
    "big_logo_image_url",
    "app_background_color",
    "overlay_image",
    "photo_filters",
    "gallery_banner",
    "header",
    "sub_header",
    "gallery_cta_text",
    "gallery_cta_link",
    "gallery_color",
    "app_background_color",
  ];
  var data = {};
  if (substate) {
    keys.map((key) => {
      if (
        ["big_logo_image_url", "overlay_image", "gallery_banner"].includes(key)
      ) {
        data[key] = urlFix(substate[key]);
      } else if (key === "photo_filters") {
        if (
          Array.isArray(substate[key]) &&
          !!substate[key][0] &&
          !!substate[key][0].file
        ) {
          data[key] = substate["photo_filters"].filter(
            (filter) => !!filter.file && filter.file.name !== null
          );
        }
      } else {
        data[key] = substate[key];
      }
    });
  }

  return data;
};
const getMessageFormData = (substate) => {
  const keys = [];
  var data = {};
  if (substate) {
    keys.map((key) => {
      data[key] = substate[key];
    });
  }

  return data;
};

const NewChannel = (props) => {
  // `location` used to arrive as a prop from <Route render={props => ...}>.
  // react-router v7 renders `element` and injects nothing, so read it from the
  // hook instead — otherwise `pathname` is undefined and the page throws.
  const { pathname } = useLocation();
  const form = useForm({
    defaultValues: {
      with_photo_filter: true,
      with_photo_filter_graphic: true,
      twitter: true,
      email: true,
      hashtag: "",
      company_promo: "",
      app_background_color: "",
      hashtag_enabled: false,
      company_promo_enabled: false,
      email_required: false,
      phone_required: false,
      twitter_required: false,
      email_enabled: true,
      phone_enabled: true,
      twitter_enabled: true,
      big_logo_image_url: null,
      gallery_color: "",
      photo_filters: null,
      sub_header: "",
      header: "",
      cta_text_link: "",
      gallery_cta_text: "",
      gallery_cta_link: "",
      share_types: [
        { required: false, enabled: false, name: "twitter" },
        { required: false, enabled: false, name: "phone" },
        { required: false, enabled: false, name: "name" },
        { required: false, enabled: false, name: "zip code" },
        { required: false, enabled: false, name: "date of birth" },
        { required: false, enabled: false, name: "email" },
        { required: false, enabled: false, name: "gallery_email" },
        { required: false, enabled: false, name: "gallery_phone" },
        { required: false, enabled: false, name: "gallery_twitter" },
        { required: false, enabled: false, name: "gallery_facebook" },
        { required: false, enabled: false, name: "first_name" },
        { required: false, enabled: false, name: "last_name" },
      ]
    },
  });

  const { postChannel, channels } = props;
  // `/channels/new` declares no :channelId, so this is undefined here — as it
  // was under v5, where it came from an empty `match.params`. Read from the
  // hook now that v7 injects no `match` prop.
  const { channelId } = useParams();
  const values = useWatch({ control: form.control });

  // Both guards sit after every hook, never before — an early return above them
  // would change the hook order between a gated and an ungated render.

  // Asked BEFORE the blocked check. On a reload the session fetch has not
  // resolved yet, so the flag is absent for a reason the fail-open read cannot
  // see — "not asked yet" is not "the server said no". Rendering the form in
  // that window hands a blocked user a live, interactive editor for as long as
  // the request takes.
  //
  // Both waiting states are NESTED under "no session yet" rather than being
  // siblings, so a loaded user cannot fall into either by construction. A
  // failure flag can outlive a good session — a later refresh failing, say —
  // and eclipsing a working session with an error is a worse lie than the flash
  // this block exists to prevent.
  if (!props.sessionUserLoaded) {
    // A transient failure never resolves on its own, so it gets a retry rather
    // than a spinner that would never stop.
    if (props.sessionLoadFailed)
      return (
        <ListError
          title="We couldn’t load your account"
          onRetry={props.retrySession}
        />
      );
    return <Loader color={colors.MAIN_COLOR} size="large" isOverlay={true} />;
  }

  // Gating the buttons is not enough on its own: this route is reachable by
  // bookmark, by a pasted link, and by a restored tab, and the server rejects
  // the save either way, so the form would only waste the work.
  // Unconditional, deliberately — unlike the EDIT route, which defers to a
  // per-channel verdict. Creation is account-scoped: `ChannelPolicy#create?`
  // has no channel_admin exemption (there is no channel yet to administer), so
  // the account-wide flag IS the whole answer here and there is nothing
  // per-channel to consult.
  if (props.channelManagementBlocked) return <SubscriptionRequiredNotice />;

  const themeId = values["channel_theme_id"];
  const channelFormLogo = values["big_logo_image"];
  const channelFormBackground = values["app_background_color"];
  const galleryColor = values["gallery_color"];
  const brandForm = getBrandFormData(values);
  const messageForm = getMessageFormData(values);

  let channel = channelId && channels ? channels[channelId] : null;

  const logoImage = isObject(channelFormLogo)
    ? makeUrl(channelFormLogo[0])
    : channel && urlFix(channel.big_logo_image_url);
  const overlayImage = isObject(channelFormBackground)
    ? makeUrl(channelFormBackground[0])
    : channel && urlFix(channel.overlay_image_url);
  const backgroundColor = isString(channelFormBackground)
    ? channelFormBackground
    : channel && channel.app_background_color;

  const bgColorChanged =
    isString(channelFormBackground) && !isObject(channelFormBackground);
  const overlayChanged =
    isObject(channelFormBackground) && !isString(channelFormBackground);

  const previewProps = {
    logoImage,
    overlayImage,
    backgroundColor,
    bgColorChanged,
    overlayChanged,
    galleryColor,
    brandForm,
    messageForm,
    themeId,
  };

  return (
    <Forms
      form={form}
      isNew
      title="New Channel"
      {...previewProps}
      onSubmit={postChannel}
    />
  );
};

const mapStateToProps = createStructuredSelector({
  channels: selectAllChannels(),
  clients: selectClients(),
  channelManagementBlocked: selectChannelManagementBlocked(),
  sessionUserLoaded: selectSessionUserLoaded(),
  sessionLoadFailed: selectSessionLoadFailed(),
});

const mapDispatchToProps = (dispatch) => ({
  postChannel: (payload) => dispatch(postChannelRequest(payload)),
  retrySession: () => dispatch(fetchSession()),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewChannel);
